import { defineDspModule, type DspModuleInstance, type DspProcessBlock, type DspParameterDescriptor } from '../../dsp-module-contract/src/index.js';

export const EQ_SECTION_TYPES = Object.freeze([
  'off', 'high-pass', 'low-pass', 'low-shelf', 'high-shelf', 'peak', 'notch', 'tilt', 'dynamic-notch',
] as const);
export type EqSectionType = (typeof EQ_SECTION_TYPES)[number];

export interface EqSectionPatch {
  readonly type?: EqSectionType;
  readonly frequencyHz?: number;
  readonly q?: number;
  readonly gainDb?: number;
  readonly dynamicThreshold?: number;
  readonly dynamicDepthDb?: number;
  readonly dynamicAttackSeconds?: number;
  readonly dynamicReleaseSeconds?: number;
}

export interface Equalizer16Options {
  readonly sampleRate: number;
  readonly smoothingSeconds?: number;
}

const SECTION_COUNT = 16;
const STAGES_PER_SECTION = 2;
const STATE_COUNT = SECTION_COUNT * STAGES_PER_SECTION;
const DENORMAL = 1e-30;
const TYPE_OFF = 0;
const TYPE_HP = 1;
const TYPE_LP = 2;
const TYPE_LOW_SHELF = 3;
const TYPE_HIGH_SHELF = 4;
const TYPE_PEAK = 5;
const TYPE_NOTCH = 6;
const TYPE_TILT = 7;
const TYPE_DYNAMIC_NOTCH = 8;

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be finite and in [${minimum}, ${maximum}]`);
  }
};

const flush = (value: number): number => Math.abs(value) < DENORMAL ? 0 : value;

export class Equalizer16 {
  readonly sampleRate: number;
  readonly sectionCount = SECTION_COUNT;
  private smoothingSeconds: number;
  private readonly types = new Int8Array(SECTION_COUNT);
  private readonly frequency = new Float64Array(SECTION_COUNT).fill(1_000);
  private readonly targetFrequency = new Float64Array(SECTION_COUNT).fill(1_000);
  private readonly frequencyStep = new Float64Array(SECTION_COUNT);
  private readonly q = new Float64Array(SECTION_COUNT).fill(Math.SQRT1_2);
  private readonly targetQ = new Float64Array(SECTION_COUNT).fill(Math.SQRT1_2);
  private readonly qStep = new Float64Array(SECTION_COUNT);
  private readonly gain = new Float64Array(SECTION_COUNT);
  private readonly targetGain = new Float64Array(SECTION_COUNT);
  private readonly gainStep = new Float64Array(SECTION_COUNT);
  private readonly rampRemaining = new Int32Array(SECTION_COUNT);
  private readonly threshold = new Float64Array(SECTION_COUNT).fill(0.5);
  private readonly depth = new Float64Array(SECTION_COUNT).fill(12);
  private readonly attack = new Float64Array(SECTION_COUNT).fill(0.005);
  private readonly release = new Float64Array(SECTION_COUNT).fill(0.08);
  private readonly envelope = new Float64Array(SECTION_COUNT);
  private readonly b0 = new Float64Array(STATE_COUNT).fill(1);
  private readonly b1 = new Float64Array(STATE_COUNT);
  private readonly b2 = new Float64Array(STATE_COUNT);
  private readonly a1 = new Float64Array(STATE_COUNT);
  private readonly a2 = new Float64Array(STATE_COUNT);
  private readonly z1 = new Float64Array(STATE_COUNT);
  private readonly z2 = new Float64Array(STATE_COUNT);

  constructor(options: Equalizer16Options) {
    assertRange('sampleRate', options.sampleRate, 8_000, 384_000);
    this.sampleRate = options.sampleRate;
    this.smoothingSeconds = options.smoothingSeconds ?? 0.01;
    assertRange('smoothingSeconds', this.smoothingSeconds, 0, 1);
    for (let section = 0; section < SECTION_COUNT; section += 1) this.updateCoefficients(section, 0);
  }

  configureSection(section: number, patch: EqSectionPatch): void {
    this.assertSection(section);
    if (patch.type !== undefined) {
      const type = EQ_SECTION_TYPES.indexOf(patch.type);
      if (type < 0) throw new RangeError('section type is invalid');
      if (this.types[section] !== type) {
        this.types[section] = type;
        this.clearSectionState(section);
      }
    }
    if (patch.frequencyHz !== undefined) {
      assertRange('frequencyHz', patch.frequencyHz, 10, this.maximumFrequency());
      this.targetFrequency[section] = patch.frequencyHz;
    }
    if (patch.q !== undefined) {
      assertRange('q', patch.q, 0.1, 20);
      this.targetQ[section] = patch.q;
    }
    if (patch.gainDb !== undefined) {
      assertRange('gainDb', patch.gainDb, -24, 24);
      this.targetGain[section] = patch.gainDb;
    }
    if (patch.dynamicThreshold !== undefined) {
      assertRange('dynamicThreshold', patch.dynamicThreshold, 0, 1);
      this.threshold[section] = patch.dynamicThreshold;
    }
    if (patch.dynamicDepthDb !== undefined) {
      assertRange('dynamicDepthDb', patch.dynamicDepthDb, 0, 36);
      this.depth[section] = patch.dynamicDepthDb;
    }
    if (patch.dynamicAttackSeconds !== undefined) {
      assertRange('dynamicAttackSeconds', patch.dynamicAttackSeconds, 0.0001, 1);
      this.attack[section] = patch.dynamicAttackSeconds;
    }
    if (patch.dynamicReleaseSeconds !== undefined) {
      assertRange('dynamicReleaseSeconds', patch.dynamicReleaseSeconds, 0.001, 5);
      this.release[section] = patch.dynamicReleaseSeconds;
    }
    this.scheduleRamp(section);
  }

  setParameter(id: string, value: number): void {
    if (id === 'smoothing-seconds') {
      assertRange(id, value, 0, 1);
      this.smoothingSeconds = value;
      return;
    }
    const match = /^section-(\d+)-(type|frequency-hz|q|gain-db|dynamic-threshold|dynamic-depth-db|dynamic-attack-seconds|dynamic-release-seconds)$/.exec(id);
    if (!match) throw new RangeError(`unknown parameter: ${id}`);
    const section = Number(match[1]);
    const parameter = match[2];
    this.assertSection(section);
    if (parameter === 'type') {
      if (!Number.isInteger(value) || value < 0 || value >= EQ_SECTION_TYPES.length) throw new RangeError('section type index is invalid');
      this.configureSection(section, { type: EQ_SECTION_TYPES[value]! });
    } else if (parameter === 'frequency-hz') this.configureSection(section, { frequencyHz: value });
    else if (parameter === 'q') this.configureSection(section, { q: value });
    else if (parameter === 'gain-db') this.configureSection(section, { gainDb: value });
    else if (parameter === 'dynamic-threshold') this.configureSection(section, { dynamicThreshold: value });
    else if (parameter === 'dynamic-depth-db') this.configureSection(section, { dynamicDepthDb: value });
    else if (parameter === 'dynamic-attack-seconds') this.configureSection(section, { dynamicAttackSeconds: value });
    else this.configureSection(section, { dynamicReleaseSeconds: value });
  }

  reset(): void {
    this.z1.fill(0);
    this.z2.fill(0);
    this.envelope.fill(0);
    this.rampRemaining.fill(0);
    this.frequency.set(this.targetFrequency);
    this.q.set(this.targetQ);
    this.gain.set(this.targetGain);
    for (let section = 0; section < SECTION_COUNT; section += 1) this.updateCoefficients(section, 0);
  }

  processInto(input: Float32Array, output: Float32Array, detector?: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames || (detector && detector.length < frames)) {
      throw new RangeError('buffers are too short for frames');
    }
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
      if (detector && !Number.isFinite(detector[frame]!)) throw new RangeError('detector samples must be finite');
    }
    for (let frame = 0; frame < frames; frame += 1) {
      let sample = input[frame]!;
      const detectorSample = detector ? detector[frame]! : 0;
      for (let section = 0; section < SECTION_COUNT; section += 1) {
        const type = this.types[section]!;
        if (type === TYPE_OFF) continue;
        if (this.rampRemaining[section]! > 0) this.advanceRamp(section);
        let dynamicGain = 0;
        if (type === TYPE_DYNAMIC_NOTCH) {
          const magnitude = Math.min(1, Math.abs(detectorSample));
          const coefficient = Math.exp(-1 / (this.sampleRate * (magnitude > this.envelope[section]! ? this.attack[section]! : this.release[section]!)));
          const envelope = magnitude + coefficient * (this.envelope[section]! - magnitude);
          this.envelope[section] = envelope;
          const threshold = this.threshold[section]!;
          const amount = threshold >= 1 ? 0 : Math.max(0, Math.min(1, (envelope - threshold) / (1 - threshold)));
          dynamicGain = -this.depth[section]! * amount;
          this.updateCoefficients(section, dynamicGain);
        }
        sample = this.processStage(section * 2, sample);
        if (type === TYPE_TILT) sample = this.processStage(section * 2 + 1, sample);
        if (!Number.isFinite(sample)) {
          this.clearSectionState(section);
          sample = 0;
        }
      }
      output[frame] = flush(sample);
    }
  }

  private maximumFrequency(): number { return this.sampleRate * 0.475; }

  private assertSection(section: number): void {
    if (!Number.isInteger(section) || section < 0 || section >= SECTION_COUNT) throw new RangeError('section index must be in [0, 15]');
  }

  private scheduleRamp(section: number): void {
    const samples = Math.round(this.smoothingSeconds * this.sampleRate);
    if (samples <= 0) {
      this.frequency[section] = this.targetFrequency[section]!;
      this.q[section] = this.targetQ[section]!;
      this.gain[section] = this.targetGain[section]!;
      this.rampRemaining[section] = 0;
      this.updateCoefficients(section, 0);
      return;
    }
    this.frequencyStep[section] = (this.targetFrequency[section]! - this.frequency[section]!) / samples;
    this.qStep[section] = (this.targetQ[section]! - this.q[section]!) / samples;
    this.gainStep[section] = (this.targetGain[section]! - this.gain[section]!) / samples;
    this.rampRemaining[section] = samples;
  }

  private advanceRamp(section: number): void {
    this.frequency[section] = this.frequency[section]! + this.frequencyStep[section]!;
    this.q[section] = this.q[section]! + this.qStep[section]!;
    this.gain[section] = this.gain[section]! + this.gainStep[section]!;
    const remaining = this.rampRemaining[section]! - 1;
    this.rampRemaining[section] = remaining;
    if (remaining === 0) {
      this.frequency[section] = this.targetFrequency[section]!;
      this.q[section] = this.targetQ[section]!;
      this.gain[section] = this.targetGain[section]!;
    }
    this.updateCoefficients(section, 0);
  }

  private clearSectionState(section: number): void {
    const stage = section * 2;
    this.z1[stage] = 0; this.z2[stage] = 0;
    this.z1[stage + 1] = 0; this.z2[stage + 1] = 0;
    this.envelope[section] = 0;
  }

  private processStage(stage: number, input: number): number {
    const output = this.b0[stage]! * input + this.z1[stage]!;
    this.z1[stage] = flush(this.b1[stage]! * input - this.a1[stage]! * output + this.z2[stage]!);
    this.z2[stage] = flush(this.b2[stage]! * input - this.a2[stage]! * output);
    return output;
  }

  /** RBJ cookbook forms are normalized by a0; every bounded Q/gain setting has poles inside the unit circle. */
  private updateCoefficients(section: number, dynamicGain: number): void {
    const type = this.types[section]!;
    const stage = section * 2;
    if (type === TYPE_OFF) {
      this.setIdentity(stage); this.setIdentity(stage + 1); return;
    }
    const frequency = Math.max(10, Math.min(this.maximumFrequency(), this.frequency[section]!));
    const omega = 2 * Math.PI * frequency / this.sampleRate;
    const cosine = Math.cos(omega);
    const sine = Math.sin(omega);
    const q = Math.max(0.1, Math.min(20, this.q[section]!));
    const alpha = sine / (2 * q);
    const gainDb = type === TYPE_DYNAMIC_NOTCH ? dynamicGain : this.gain[section]!;
    const amplitude = 10 ** (gainDb / 40);
    let b0 = 1; let b1 = 0; let b2 = 0; let a0 = 1; let a1 = 0; let a2 = 0;

    if (type === TYPE_HP) {
      b0 = (1 + cosine) / 2; b1 = -(1 + cosine); b2 = b0;
      a0 = 1 + alpha; a1 = -2 * cosine; a2 = 1 - alpha;
    } else if (type === TYPE_LP) {
      b0 = (1 - cosine) / 2; b1 = 1 - cosine; b2 = b0;
      a0 = 1 + alpha; a1 = -2 * cosine; a2 = 1 - alpha;
    } else if (type === TYPE_PEAK || type === TYPE_DYNAMIC_NOTCH) {
      b0 = 1 + alpha * amplitude; b1 = -2 * cosine; b2 = 1 - alpha * amplitude;
      a0 = 1 + alpha / amplitude; a1 = -2 * cosine; a2 = 1 - alpha / amplitude;
    } else if (type === TYPE_NOTCH) {
      b0 = 1; b1 = -2 * cosine; b2 = 1;
      a0 = 1 + alpha; a1 = -2 * cosine; a2 = 1 - alpha;
    } else if (type === TYPE_LOW_SHELF || type === TYPE_HIGH_SHELF) {
      const shelfAlpha = sine / Math.SQRT2;
      const root = 2 * Math.sqrt(amplitude) * shelfAlpha;
      if (type === TYPE_LOW_SHELF) {
        b0 = amplitude * ((amplitude + 1) - (amplitude - 1) * cosine + root);
        b1 = 2 * amplitude * ((amplitude - 1) - (amplitude + 1) * cosine);
        b2 = amplitude * ((amplitude + 1) - (amplitude - 1) * cosine - root);
        a0 = (amplitude + 1) + (amplitude - 1) * cosine + root;
        a1 = -2 * ((amplitude - 1) + (amplitude + 1) * cosine);
        a2 = (amplitude + 1) + (amplitude - 1) * cosine - root;
      } else {
        b0 = amplitude * ((amplitude + 1) + (amplitude - 1) * cosine + root);
        b1 = -2 * amplitude * ((amplitude - 1) + (amplitude + 1) * cosine);
        b2 = amplitude * ((amplitude + 1) + (amplitude - 1) * cosine - root);
        a0 = (amplitude + 1) - (amplitude - 1) * cosine + root;
        a1 = 2 * ((amplitude - 1) - (amplitude + 1) * cosine);
        a2 = (amplitude + 1) - (amplitude - 1) * cosine - root;
      }
    } else if (type === TYPE_TILT) {
      this.setShelf(stage, cosine, sine, -gainDb / 2, true);
      this.setShelf(stage + 1, cosine, sine, gainDb / 2, false);
      return;
    }
    this.setNormalized(stage, b0, b1, b2, a0, a1, a2);
    this.setIdentity(stage + 1);
  }

  private setShelf(stage: number, cosine: number, sine: number, gainDb: number, low: boolean): void {
    const amplitude = 10 ** (gainDb / 40);
    const alpha = sine / Math.SQRT2;
    const root = 2 * Math.sqrt(amplitude) * alpha;
    if (low) {
      this.setNormalized(stage,
        amplitude * ((amplitude + 1) - (amplitude - 1) * cosine + root),
        2 * amplitude * ((amplitude - 1) - (amplitude + 1) * cosine),
        amplitude * ((amplitude + 1) - (amplitude - 1) * cosine - root),
        (amplitude + 1) + (amplitude - 1) * cosine + root,
        -2 * ((amplitude - 1) + (amplitude + 1) * cosine),
        (amplitude + 1) + (amplitude - 1) * cosine - root);
    } else {
      this.setNormalized(stage,
        amplitude * ((amplitude + 1) + (amplitude - 1) * cosine + root),
        -2 * amplitude * ((amplitude - 1) + (amplitude + 1) * cosine),
        amplitude * ((amplitude + 1) + (amplitude - 1) * cosine - root),
        (amplitude + 1) - (amplitude - 1) * cosine + root,
        2 * ((amplitude - 1) - (amplitude + 1) * cosine),
        (amplitude + 1) - (amplitude - 1) * cosine - root);
    }
  }

  private setNormalized(stage: number, b0: number, b1: number, b2: number, a0: number, a1: number, a2: number): void {
    this.b0[stage] = b0 / a0; this.b1[stage] = b1 / a0; this.b2[stage] = b2 / a0;
    this.a1[stage] = a1 / a0; this.a2[stage] = a2 / a0;
  }

  private setIdentity(stage: number): void {
    this.b0[stage] = 1; this.b1[stage] = 0; this.b2[stage] = 0; this.a1[stage] = 0; this.a2[stage] = 0;
  }
}

class EqualizerModuleInstance implements DspModuleInstance {
  private readonly equalizer: Equalizer16;
  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.equalizer = new Equalizer16({ sampleRate });
  }
  reset(): void { this.equalizer.reset(); }
  setParameter(id: string, value: number): void { this.equalizer.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const input = block.inputs.in;
    const output = block.outputs.out;
    const detector = block.controls.detector;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    if (detector && detector.length < block.frames) throw new RangeError('detector buffer is too short');
    this.equalizer.processInto(input, output, detector, block.frames);
  }
}

const parameterDescriptors: DspParameterDescriptor[] = [
  { id: 'smoothing-seconds', defaultValue: 0.01, minValue: 0, maxValue: 1, unit: 's' },
];
for (let section = 0; section < SECTION_COUNT; section += 1) {
  parameterDescriptors.push(
    { id: `section-${section}-type`, defaultValue: 0, minValue: 0, maxValue: EQ_SECTION_TYPES.length - 1, unit: 'enum' },
    { id: `section-${section}-frequency-hz`, defaultValue: 1_000, minValue: 10, maxValue: 182_400, unit: 'Hz' },
    { id: `section-${section}-q`, defaultValue: Math.SQRT1_2, minValue: 0.1, maxValue: 20, unit: 'ratio' },
    { id: `section-${section}-gain-db`, defaultValue: 0, minValue: -24, maxValue: 24, unit: 'dB' },
    { id: `section-${section}-dynamic-threshold`, defaultValue: 0.5, minValue: 0, maxValue: 1, unit: 'linear' },
    { id: `section-${section}-dynamic-depth-db`, defaultValue: 12, minValue: 0, maxValue: 36, unit: 'dB' },
    { id: `section-${section}-dynamic-attack-seconds`, defaultValue: 0.005, minValue: 0.0001, maxValue: 1, unit: 's' },
    { id: `section-${section}-dynamic-release-seconds`, defaultValue: 0.08, minValue: 0.001, maxValue: 5, unit: 's' },
  );
}

export const mod021 = defineDspModule({
  descriptor: {
    id: 'mir.mod-021',
    name: 'Sixteen-section parametric equalizer',
    version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }, { id: 'detector', kind: 'control', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: parameterDescriptors,
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate < 8_000 || context.sampleRate > 384_000 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new EqualizerModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
